/**
 * SessionConnectionChips - the featured session card's connection summary.
 *
 * Replaces the old bare "4 connections" count: one chip per connection the
 * session declares (name + relative last-active age when derivable), plus a
 * caption counting what a resume will actually bring back.
 *
 * A chip says nothing about WHEN its connection opens, because there is nothing
 * left to say: the resume opens every one of them and shows you each as it
 * happens. "connects now" / "in background" described the old split, where half
 * the session was handed to a silent background sweep; the labels outlived it
 * and only invited the question of why one connection was singled out.
 *
 * The one state still worth a word is "needs password": that connection is not
 * dialed with the others (the prompt is single-instance) and genuinely waits
 * for the first tab that needs it, so the chip has to say so rather than let
 * the user expect a connect that isn't coming.
 *
 * When the card resumes, each chip is a toggle: switching one off excludes
 * that connection from the resume (the dialog routes exclusions through the
 * existing `remove` decision path, which drops the connection *and its tabs*
 * from the session — hence the tab warning in the caption). When the card only
 * returns to an already-live session, the chips are plain information.
 */
import { Database, RotateCcw, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatTimeAgo, type ConnectionInfo } from '../utils/sessionDisplayUtils';

interface SessionConnectionChipsProps {
  /** Connections the session declares, in display order. */
  connections: ConnectionInfo[];
  /** Ids the user switched off — still listed, so they can be switched back on. */
  excludedConnectionIds: string[];
  /** False when the card only closes the dialog: chips become read-only. */
  canExclude: boolean;
  /** Session tabs that would be dropped along with the excluded connections. */
  excludedTabCount: number;
  /**
   * The connections the resume will NOT open with the rest, because opening
   * them raises the single-instance password prompt. The only chips that
   * promise nothing until the user goes looking.
   */
  passwordConnectionIds?: string[];
  onToggleExcluded: (connectionId: string, excluded: boolean) => void;
}

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors';

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function captionText(
  total: number,
  included: number,
  canExclude: boolean,
  excludedTabCount: number
): string {
  if (total === 0) return 'No connections';
  if (!canExclude) return plural(total, 'connection');
  const head =
    included === 0
      ? 'No connections will be restored'
      : `Restores ${included} of ${plural(total, 'connection')}`;
  return excludedTabCount > 0
    ? `${head} · ${plural(excludedTabCount, 'tab')} left behind`
    : head;
}

export function SessionConnectionChips({
  connections,
  excludedConnectionIds,
  canExclude,
  excludedTabCount,
  passwordConnectionIds,
  onToggleExcluded,
}: SessionConnectionChipsProps) {
  const excluded = new Set(excludedConnectionIds);
  const passwordGated = new Set(passwordConnectionIds ?? []);
  const includedCount = connections.filter((conn) => !excluded.has(conn.id)).length;

  // Only meaningful for a card that will actually resume, and only for
  // connections that are coming back at all. Everything else is silent: the
  // resume opens it like the rest, so a label would only be noise.
  const stateCaption = (connectionId: string): string | null => {
    if (!canExclude || excluded.has(connectionId)) return null;
    return passwordGated.has(connectionId) ? 'needs password' : null;
  };

  return (
    <div className="space-y-1.5">
      {connections.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid="session-connection-chips"
        >
          {connections.map((conn) => {
            const isExcluded = excluded.has(conn.id);
            const age = conn.lastConnectedAt ? formatTimeAgo(conn.lastConnectedAt) : null;
            const caption = stateCaption(conn.id);
            const label = (
              <>
                <Database className="h-3 w-3 shrink-0" />
                <span className="truncate max-w-[10rem]">{conn.name}</span>
                {age && <span className="text-muted-foreground tabular-nums">{age}</span>}
                {caption && (
                  <span
                    className="text-muted-foreground border-l border-border/60 pl-1.5"
                    data-testid="session-connection-chip-state"
                  >
                    {caption}
                  </span>
                )}
              </>
            );

            if (!canExclude) {
              return (
                <span
                  key={conn.id}
                  className={cn(CHIP_BASE, 'border-border bg-muted/40 text-foreground')}
                  data-testid="session-connection-chip"
                  data-connection-id={conn.id}
                  data-excluded="false"
                  title={conn.name}
                >
                  {label}
                </span>
              );
            }

            return (
              <button
                key={conn.id}
                type="button"
                aria-pressed={!isExcluded}
                onClick={() => onToggleExcluded(conn.id, !isExcluded)}
                className={cn(
                  CHIP_BASE,
                  'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/30 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                  isExcluded
                    ? 'border-dashed border-border text-muted-foreground/70 line-through'
                    : 'border-border bg-muted/40 text-foreground hover:bg-muted'
                )}
                data-testid="session-connection-chip"
                data-connection-id={conn.id}
                data-excluded={isExcluded}
                title={
                  isExcluded
                    ? `Include ${conn.name} when resuming`
                    : `Don't restore ${conn.name}`
                }
              >
                {label}
                {isExcluded ? (
                  <RotateCcw className="h-3 w-3 shrink-0" />
                ) : (
                  <X className="h-3 w-3 shrink-0 opacity-60" />
                )}
                <span className="sr-only">
                  {isExcluded ? 'Excluded from resume' : 'Included in resume'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <p
        className="text-sm text-muted-foreground"
        data-testid="session-connections-caption"
      >
        {captionText(connections.length, includedCount, canExclude, excludedTabCount)}
      </p>
    </div>
  );
}
